import { formatEUR } from "@/lib/format";

/**
 * Scène de démonstration du site : une seule entreprise fictive qui relance,
 * les mêmes clients d'une section à l'autre. Toutes les données sont
 * fictives et affichées comme telles (« Données fictives »).
 */

export const DEMO_SENDER = {
  person: "Claire Martin",
  company: "Fournitures Dalbran",
  email: "claire.martin@fournitures-dalbran.fr",
} as const;

export type DemoClient = {
  id: string;
  name: string;
  contact: string;
  invoices: number;
  amount: number;
  /** Jours de retard sur la facture la plus ancienne. */
  days: number;
  /** Une relance est prévue aujourd'hui (ou en retard). */
  dueToday: boolean;
  /** Aucun échange depuis longtemps : badge « oublié ». */
  neglected: boolean;
  /** Dernier échange noté dans la fiche. */
  note: string;
  /** Date promise par le client, s'il en a donné une. */
  promise?: string;
  /** Le client a répondu : la réponse est rangée dans sa fiche. */
  replied?: boolean;
};

export const DEMO_CLIENTS: ReadonlyArray<DemoClient> = [
  {
    id: "rivage",
    name: "Atelier du Rivage",
    contact: "compta@atelier-du-rivage.fr",
    invoices: 3,
    amount: 4280,
    days: 46,
    dueToday: false,
    neglected: false,
    note: "Appel du 24/09 : règlement annoncé pour le 2 octobre.",
    promise: "02/10",
  },
  {
    id: "arbelot",
    name: "Menuiseries Arbelot",
    contact: "j.lefevre@menuiseries-arbelot.fr",
    invoices: 2,
    amount: 12480,
    days: 74,
    dueToday: true,
    neglected: false,
    note: "Deuxième relance prévue aujourd’hui.",
  },
  {
    id: "monts",
    name: "Coopérative des Monts",
    contact: "gestion@coop-des-monts.fr",
    invoices: 1,
    amount: 2730,
    days: 38,
    dueToday: false,
    neglected: true,
    note: "Aucun échange depuis 21 jours.",
  },
  {
    id: "rhone",
    name: "Transports Rhône-Est",
    contact: "direction@transports-rhone-est.fr",
    invoices: 4,
    amount: 31000,
    days: 196,
    dueToday: false,
    neglected: false,
    note: "Relance ferme du 10/09 restée sans réponse.",
  },
  {
    id: "vauban",
    name: "Imprimerie Vauban",
    contact: "factures@imprimerie-vauban.fr",
    invoices: 1,
    amount: 5610,
    days: 63,
    dueToday: false,
    neglected: false,
    note: "A demandé un duplicata de la facture.",
  },
  {
    id: "meridien",
    name: "Studio Méridien",
    contact: "bonjour@studio-meridien.fr",
    invoices: 1,
    amount: 1250.5,
    days: 12,
    dueToday: false,
    neglected: false,
    note: "A répondu hier : virement en cours.",
    replied: true,
  },
];

/** Les trois factures d'Atelier du Rivage (4 280,00 € au total). */
export const DEMO_RIVAGE_INVOICES: ReadonlyArray<{
  number: string;
  issued: string;
  due: string;
  amount: number;
}> = [
  { number: "F-2026-104", issued: "12/07/2026", due: "11/08/2026", amount: 1520 },
  { number: "F-2026-117", issued: "27/07/2026", due: "26/08/2026", amount: 1240 },
  { number: "F-2026-131", issued: "10/08/2026", due: "09/09/2026", amount: 1520 },
];

export type WorklistOrder = "jour" | "montant" | "anciennete";

/**
 * La file « À relancer » de l'app écarte les clients qui ont une promesse
 * de paiement en cours ou une réponse à traiter (`isToRelaunchToday`).
 */
export function toRelaunch(rows: readonly DemoClient[]): DemoClient[] {
  return rows.filter((c) => !c.promise && !c.replied);
}

/**
 * Ordre de la liste « À relancer », comme dans l'app : les relances prévues
 * aujourd'hui d'abord, puis les clients oubliés, puis du plus ancien retard
 * au plus récent. Les autres ordres sont les tris de colonne.
 */
export function sortWorklist(
  rows: ReadonlyArray<DemoClient>,
  order: WorklistOrder,
): DemoClient[] {
  const rank = (r: DemoClient) => (r.dueToday ? 0 : r.neglected ? 1 : 2);
  return [...rows].sort((a, b) => {
    if (order === "montant") return b.amount - a.amount;
    if (order === "anciennete") return b.days - a.days;
    return rank(a) - rank(b) || b.days - a.days;
  });
}

export type DemoTemplate = {
  id: string;
  name: string;
  subject: string;
  paragraphs: ReadonlyArray<string>;
};

/**
 * Quatre des six modèles fournis à chaque organisation (migration
 * `20260730120000_email_templates.sql`), mot pour mot. Le tableau des
 * factures et la signature sont ajoutés à l'envoi.
 */
export const DEMO_TEMPLATES: ReadonlyArray<DemoTemplate> = [
  {
    id: "amiable",
    name: "Relance amiable",
    subject: "Relance — {{client}} — {{montant}} en attente",
    paragraphs: [
      "Bonjour,",
      "Sauf erreur de notre part, votre compte présente un solde impayé de {{montant}}. Vous trouverez ci-dessous le détail des factures concernées, ainsi que les PDFs en pièce jointe.",
      "Pourriez-vous nous indiquer la date de règlement prévue, ou revenir vers nous en cas de difficulté de votre côté ?",
      "Bien cordialement,",
    ],
  },
  {
    id: "deuxieme",
    name: "Deuxième relance",
    subject: "Deuxième relance — {{client}} — {{montant}}",
    paragraphs: [
      "Bonjour,",
      "Nous revenons vers vous concernant le solde de {{montant}}, resté sans règlement à ce jour malgré notre précédent message.",
      "Merci de nous confirmer la date de paiement, ou de nous signaler tout élément qui bloquerait de votre côté.",
      "Bien cordialement,",
    ],
  },
  {
    id: "ferme",
    name: "Relance ferme",
    subject: "Relance — {{client}} — règlement attendu sous 8 jours",
    paragraphs: [
      "Bonjour,",
      "Malgré nos relances, le solde de {{montant}} demeure impayé, avec un retard atteignant {{jours_retard}} jours.",
      "Nous vous demandons de procéder au règlement sous 8 jours. À défaut, nous serons contraints de suspendre les prestations en cours.",
      "Bien cordialement,",
    ],
  },
  {
    id: "echeancier",
    name: "Proposition d’échéancier",
    subject: "Proposition d’échéancier — {{client}}",
    paragraphs: [
      "Bonjour,",
      "Le solde de votre compte s’élève à {{montant}}. Nous comprenons qu’un règlement en une fois puisse être difficile.",
      "Nous vous proposons d’échelonner ce montant sur plusieurs mensualités. Dites-nous le rythme qui vous conviendrait, nous vous adresserons un échéancier à valider.",
      "Bien cordialement,",
    ],
  },
];

export function fillTemplate(
  template: DemoTemplate,
  ctx: { client: string; amount: number; days: number },
): { subject: string; paragraphs: string[] } {
  const fill = (text: string) =>
    text
      .replaceAll("{{client}}", ctx.client)
      .replaceAll("{{montant}}", formatEUR(ctx.amount))
      .replaceAll("{{jours_retard}}", String(ctx.days));
  return {
    subject: fill(template.subject),
    paragraphs: template.paragraphs.map(fill),
  };
}

/** Seuil par défaut de `organization_settings.late_penalty_threshold_days`. */
export const LATE_NOTICE_THRESHOLD = 180;

/**
 * La préparation de la mise en demeure reste fermée tant que la créance n'a
 * pas dépassé le seuil (règle appliquée côté serveur dans l'app).
 */
export function lateNoticeAvailable(
  days: number,
  threshold: number = LATE_NOTICE_THRESHOLD,
): boolean {
  return days > threshold;
}
